import { z } from 'zod';

/**
 * The controls the admin settings surface knows how to render (W8).
 *
 * The settings UI is generated **entirely** from registry metadata — there is
 * no hand-built form for an individual key — so this union is the contract
 * between a definition and the admin kit.
 *
 * **The union is closed, and closed in both directions.** Every member has at
 * least one real definition *and* a renderer: the admin's `settingFieldFor`
 * mapping is exhaustive over this list with a `never`-typed default, so adding
 * a member here **fails the admin typecheck** until an arm exists for it, and a
 * member no definition uses is removed rather than left as an option nobody has
 * ever rendered (`textarea` and `multi-select` went that way in P10.3a — no
 * setting is prose, and there is no array-shaped setting type, deliberately).
 */
export const SETTING_CONTROLS = [
  'switch',
  'number',
  'text',
  'select',
  'toggle-group',
  'key-value',
  'json',
  'color',
  // Added in P10.2c. Each one exists because a bare `number` or `text` box
  // misrepresents the domain it was standing in for: an integer whose unit is
  // invisible, a 0–1 fraction one keystroke away from being ten times wrong, and
  // a format string whose rendering the operator could not see.
  'duration',
  'percent',
  'pattern',
  // Added in P10.2e. A setting whose value is a media asset id: a uuid in a text
  // box is a value no operator can produce and nobody can check — the control
  // uploads, previews and picks, and the definition says which media kind it may
  // point at (`mediaKind`).
  'media',
  // Added in P10.3b. A value whose shape is FIXED and NAMED — `brand.legalEntity`
  // is four declared strings, not an open map — is edited property by property in
  // a real fieldset. `json` and `key-value` remain the controls for a value whose
  // keys are data rather than schema; the api's `defineSetting` refuses this
  // control for anything but a `ZodObject` of scalars, so the admin can always
  // draw a real control for every property.
  'object',
  // Added in P30b2. A SET of countries is a record `{ SA: true }` merged over a
  // coded default of `{}` (P26b: stored layers overlay the default, so an
  // empty coded default is what keeps every key removable) — still no
  // array-shaped setting. Drawn as flag chips plus a picker, "any country" when
  // empty. `country` is one nullable code, drawn by the existing country picker.
  'country-set',
  'country',
] as const;

export const SettingControlSchema = z.enum(SETTING_CONTROLS);

export type SettingControl = (typeof SETTING_CONTROLS)[number];

/**
 * One choice of a `select` / `toggle-group` control.
 *
 * The label is an **i18n key** (U8): a literal here would be untranslatable
 * copy shipped from the server, and the admin already owns ar/en catalogs.
 */
export const SettingSelectOptionSchema = z
  .object({
    value: z.string().min(1),
    labelKey: z.string().min(1),
  })
  .strict();

export type SettingSelectOption = z.infer<typeof SettingSelectOptionSchema>;
